/**
 * Demo2-only persona overlay for the Lender Members API payload.
 *
 * Why this exists:
 *   The Demo CU contract on the API redacts `members` PII (names via the
 *   `initials_only` rule "Demo" -> "D.", "MemberN" -> "M.") and does not
 *   carry the richer `members` columns (photo, age, inst_loan_advisor,
 *   inst_member_since, dashboard_status) through the lender summary view.
 *   The API repo has no Demo2 branch, so any backend fix carries shared
 *   Dev/QA/Prod blast radius. This frontend overlay rehydrates the canonical
 *   synthetic-persona fields by `member_id` for the 20 Demo CU members.
 *
 * Scope: branch `demo2` only.
 * Source of truth: `verascore-docs/00-START-HERE/Demo2Data.txt` (CPTO-run SQL
 *   pack `DEMO2-FULL-PERSONA-OVERLAY-SQL-PACK.md`, Q0/Q1/Q2/Q7).
 * Behavior: the overlay only fills in fields that are redacted-to-initials,
 *   null, or zero on the live API row. Real API values always win. The map
 *   itself is the org scoping mechanism — only Demo CU UUIDs are present, so
 *   any other org's members pass through unchanged.
 */

import type { LenderMember } from './lenderApiService';

const PHOTO_BASE =
  'https://xfwnkspsttlvdcklrbvw.supabase.co/storage/v1/object/public/member-photos';

interface DemoPersona {
  // Display / identification
  firstName: string;
  lastName: string;
  displayName: string;
  demoLabel: string;
  avatarLabel: string;

  // Profile (from `members`)
  photoUrl?: string;
  city?: string;
  state?: string;
  age?: number;
  loanAdvisor?: string;
  memberSince?: string;       // inst_member_since (YYYY-MM-DD)
  dashboardStatus?: string;   // 'Dashboard Ready' | 'Needs Attention' | 'Awaiting Dashboard'

  // Contact (Phase 12B). Email + zipcode mirror DB values (Query B/C found
  // 100% populated in `public.members`) so the overlay can surface them when
  // the lender summary view strips them. Phone is synthetic — DB column is
  // null for all 20 Demo CU members. Numbers use FCC-reserved 555 prefix.
  email?: string;
  phoneNumber?: string;       // E.164-shaped synthetic, e.g. "+15550101001"
  zipcode?: string;           // 5-digit or ZIP+4 (stored raw; UI formats)

  // Score (from `lender_member_summary`). Omitted for Member27 (no official
  // score on file — see Demo2Data Q1; v2 operational score intentionally
  // NOT promoted to "official" per CPTO officialization contract).
  verascore?: number;
  verascoreRating?: string;
  scoreMonth?: string;
  sixMonthScore?: number;
  twelveMonthScore?: number;
  twentyFourMonthScore?: number;

  // Income. For the 19 scored members these mirror the lender view's
  // DEPOSITED_NET basis. For Member27 these come from the documented
  // `financial_health_stats_v2.gross_income * 12` fallback.
  yearlyGrossIncome?: number;
  monthlyGrossIncome?: number;
  depositedIncome?: number;
  incomeBasisUsed?: 'ATTESTED' | 'DEPOSITED_NET' | 'VERIFIED_GROSS';
  incomeConfidence?: number;

  // Documentation-only flag; surfaces the "Awaiting official score" copy
  // intent for the no-official-score case (Member27). No UI consumer is
  // currently wired — the existing `determineDashboardStatus` path handles
  // the empty-score case visually.
  scoreDisplayLabel?: string;

  // Synthetic credit score (650–760 band) used to populate the dashboard
  // member card, the Score Details / Current Finances credit-score tile, and
  // the AI Guidance VeraScore-vs-Credit-Score comparison. The Demo CU
  // `credit_scores` table is empty (no third-party pull), so without this
  // overlay the UI either shows "Not Pulled" or falls back to a deterministic
  // mock. Demo2 is synthetic by definition; `creditScoreSource` records that.
  creditScore?: number;
  creditScoreSource?: 'DEMO_SYNTHETIC';
}

// 20 Demo CU personas keyed by member_id (uuid from the live
// /api/verascore/lender/members payload for org_id 06c80a56...).
// Values come from Demo2Data.txt (Q0 members snapshot + Q7 overlay export).
const DEMO2_PERSONA_MAP: Record<string, DemoPersona> = {
  // Member12 — Marcus Brown
  '0e891553-2798-4a20-9a67-93683dce0d01': {
    firstName: 'Marcus', lastName: 'Brown',
    displayName: 'Marcus Brown', demoLabel: 'Demo Member12', avatarLabel: 'MB',
    photoUrl: `${PHOTO_BASE}/male_african_american_1.png`,
    city: 'East Catherineborough', state: 'NE', age: 37,
    loanAdvisor: 'Deborah Bruce', memberSince: '2016-08-14',
    dashboardStatus: 'Dashboard Ready',
    email: 'demo.member12@verascore.test', phoneNumber: '+15550101001', zipcode: '207690438',
    verascore: 57, verascoreRating: 'Fair', scoreMonth: '2026-04',
    sixMonthScore: 48, twelveMonthScore: 45, twentyFourMonthScore: 41,
    yearlyGrossIncome: 109980, monthlyGrossIncome: 9165.00, depositedIncome: 109980,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 681, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member14 — Victor Smith
  '125534fc-01f1-4baf-a4da-ae2423c12281': {
    firstName: 'Victor', lastName: 'Smith',
    displayName: 'Victor Smith', demoLabel: 'Demo Member14', avatarLabel: 'VS',
    photoUrl: `${PHOTO_BASE}/male_asian_3.png`,
    city: 'Lake Justin', state: 'KS', age: 61,
    loanAdvisor: 'Dennis Nichols MD', memberSince: '2022-05-25',
    dashboardStatus: 'Dashboard Ready',
    email: 'demo.member14@verascore.test', phoneNumber: '+15550101002', zipcode: '207462275',
    verascore: 45, verascoreRating: 'Fair', scoreMonth: '2026-04',
    sixMonthScore: 45, twelveMonthScore: 44, twentyFourMonthScore: 48,
    yearlyGrossIncome: 24024, monthlyGrossIncome: 2002.00, depositedIncome: 24024,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 672, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member16 — Edward Hudson
  '044b0a36-cc5a-4e8d-9d01-287fa372a97f': {
    firstName: 'Edward', lastName: 'Hudson',
    displayName: 'Edward Hudson', demoLabel: 'Demo Member16', avatarLabel: 'EH',
    photoUrl: `${PHOTO_BASE}/male_hispanic_2.png`,
    city: 'Lopezfurt', state: 'NE', age: 26,
    loanAdvisor: 'Brianna Miller', memberSince: '2017-09-22',
    dashboardStatus: 'Needs Attention',
    email: 'demo.member16@verascore.test', phoneNumber: '+15550101003', zipcode: '200193557',
    verascore: 25, verascoreRating: 'Poor', scoreMonth: '2026-04',
    sixMonthScore: 31, twelveMonthScore: 29, twentyFourMonthScore: 27,
    yearlyGrossIncome: 63615.72, monthlyGrossIncome: 5301.31, depositedIncome: 63615.72,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 651, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member22 — Nathan Reed
  '21f4057c-a9db-4a8f-ba47-3ab5091a2aa3': {
    firstName: 'Nathan', lastName: 'Reed',
    displayName: 'Nathan Reed', demoLabel: 'Demo Member22', avatarLabel: 'NR',
    photoUrl: `${PHOTO_BASE}/male_native_american_2.png`,
    city: 'West Rebeccamouth', state: 'IN', age: 32,
    loanAdvisor: 'Lisa Smith', memberSince: '2019-02-28',
    dashboardStatus: 'Dashboard Ready',
    email: 'demo.member22@verascore.test', phoneNumber: '+15550101004', zipcode: '200013533',
    verascore: 30, verascoreRating: 'Poor', scoreMonth: '2026-04',
    sixMonthScore: 28, twelveMonthScore: 35, twentyFourMonthScore: 39,
    yearlyGrossIncome: 54999.96, monthlyGrossIncome: 4583.33, depositedIncome: 54999.96,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 660, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member27 — Andre Weiss (NO official score; income from FHS v2 fallback)
  '10c089a5-061b-46fc-bb01-c104c671c059': {
    firstName: 'Andre', lastName: 'Weiss',
    displayName: 'Andre Weiss', demoLabel: 'Demo Member27', avatarLabel: 'AW',
    photoUrl: `${PHOTO_BASE}/male_african_american_1.png`,
    city: 'South Coreyside', state: 'KS', age: 23,
    loanAdvisor: 'Carla Roth', memberSince: '2017-04-11',
    dashboardStatus: 'Dashboard Ready',
    email: 'demo.member27@verascore.test', phoneNumber: '+15550101005', zipcode: '207431009',
    // No verascore / verascoreRating / scoreMonth / 6m / 12m / 24m — Andre Weiss
    // has zero officialized rows in lender_member_summary (Demo2Data Q1/Q5).
    // Operational v2 score (93) intentionally NOT promoted to official.
    yearlyGrossIncome: 83000.04, monthlyGrossIncome: 6916.67, depositedIncome: 83000.04,
    scoreDisplayLabel: 'Awaiting official score',
    creditScore: 688, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member41 — Dasian Brown
  'a36c1d99-ed68-481d-bb61-737d3d9746ed': {
    firstName: 'Dasian', lastName: 'Brown',
    displayName: 'Dasian Brown', demoLabel: 'Demo Member41', avatarLabel: 'DB',
    photoUrl: `${PHOTO_BASE}/male_hispanic_1.png`,
    city: 'West Denisechester', state: 'WV', age: 42,
    loanAdvisor: 'Steven Gutierrez', memberSince: '2019-06-28',
    dashboardStatus: 'Dashboard Ready',
    email: 'demo.member41@verascore.test', phoneNumber: '+15550101006', zipcode: '200205716',
    verascore: 88, verascoreRating: 'Excellent', scoreMonth: '2026-04',
    sixMonthScore: 76, twelveMonthScore: 71, twentyFourMonthScore: 67,
    yearlyGrossIncome: 52694.76, monthlyGrossIncome: 4391.23, depositedIncome: 52694.76,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 742, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member45 — Cheryl Stevens
  '0ebbde36-80fd-4f63-85de-bdd778b6ba16': {
    firstName: 'Cheryl', lastName: 'Stevens',
    displayName: 'Cheryl Stevens', demoLabel: 'Demo Member45', avatarLabel: 'CS',
    photoUrl: `${PHOTO_BASE}/female_asian_2.png`,
    city: 'East Jessica', state: 'NE', age: 49,
    loanAdvisor: 'Katherine White', memberSince: '2019-09-06',
    dashboardStatus: 'Dashboard Ready',
    email: 'demo.member45@verascore.test', phoneNumber: '+15550101007', zipcode: '206034783',
    verascore: 32, verascoreRating: 'Poor', scoreMonth: '2026-04',
    sixMonthScore: 34, twelveMonthScore: 32, twentyFourMonthScore: 40,
    yearlyGrossIncome: 97430.04, monthlyGrossIncome: 8119.17, depositedIncome: 97430.04,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 666, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member49 — Thomas Bradley
  '3fb576c5-4a28-41cf-91c0-5fb540c5db81': {
    firstName: 'Thomas', lastName: 'Bradley',
    displayName: 'Thomas Bradley', demoLabel: 'Demo Member49', avatarLabel: 'TB',
    photoUrl: `${PHOTO_BASE}/male_hispanic_3.png`,
    city: 'Stevenville', state: 'ME', age: 28,
    loanAdvisor: 'Nathaniel Howell', memberSince: '2021-07-30',
    dashboardStatus: 'Dashboard Ready',
    email: 'demo.member49@verascore.test', phoneNumber: '+15550101008', zipcode: '207442243',
    verascore: 29, verascoreRating: 'Poor', scoreMonth: '2026-04',
    sixMonthScore: 30, twelveMonthScore: 34, twentyFourMonthScore: 44,
    yearlyGrossIncome: 172074.96, monthlyGrossIncome: 14339.58, depositedIncome: 172074.96,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 655, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member51 — Seth Khan
  '3f296ec3-4fef-40d4-9299-ca7d0e1eaa57': {
    firstName: 'Seth', lastName: 'Khan',
    displayName: 'Seth Khan', demoLabel: 'Demo Member51', avatarLabel: 'SK',
    photoUrl: `${PHOTO_BASE}/male_asian_1.png`,
    city: 'Bryantown', state: 'IL', age: 45,
    loanAdvisor: 'Christine Morales', memberSince: '2021-05-20',
    dashboardStatus: 'Dashboard Ready',
    email: 'demo.member51@verascore.test', phoneNumber: '+15550101009', zipcode: '207726713',
    verascore: 43, verascoreRating: 'Fair', scoreMonth: '2026-04',
    sixMonthScore: 42, twelveMonthScore: 46, twentyFourMonthScore: 41,
    yearlyGrossIncome: 111960, monthlyGrossIncome: 9330.00, depositedIncome: 111960,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 674, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member52 — Amanda Williams
  '229b3ca1-f8b7-4a5a-9a66-9adb898543fd': {
    firstName: 'Amanda', lastName: 'Williams',
    displayName: 'Amanda Williams', demoLabel: 'Demo Member52', avatarLabel: 'AW',
    photoUrl: `${PHOTO_BASE}/female_asian_1.png`,
    city: 'Nicholasshire', state: 'NV', age: 31,
    loanAdvisor: 'Dr. Brenda Garcia', memberSince: '2020-09-12',
    dashboardStatus: 'Needs Attention',
    email: 'demo.member52@verascore.test', phoneNumber: '+15550101010', zipcode: '217406996',
    verascore: 57, verascoreRating: 'Fair', scoreMonth: '2026-04',
    sixMonthScore: 42, twelveMonthScore: 37, twentyFourMonthScore: 35,
    yearlyGrossIncome: 121065.00, monthlyGrossIncome: 10088.75, depositedIncome: 121065.00,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 698, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member56 — Christine Bowman
  'f9a1308b-f758-4150-b9e7-51e31d1c573f': {
    firstName: 'Christine', lastName: 'Bowman',
    displayName: 'Christine Bowman', demoLabel: 'Demo Member56', avatarLabel: 'CB',
    photoUrl: `${PHOTO_BASE}/female_white_3.png`,
    city: 'Jacksonstad', state: 'WA', age: 33,
    loanAdvisor: 'Michael Fletcher', memberSince: '2018-11-08',
    dashboardStatus: 'Dashboard Ready',
    email: 'demo.member56@verascore.test', phoneNumber: '+15550101011', zipcode: '207725111',
    verascore: 40, verascoreRating: 'Fair', scoreMonth: '2026-04',
    sixMonthScore: 50, twelveMonthScore: 53, twentyFourMonthScore: 54,
    yearlyGrossIncome: 71037.96, monthlyGrossIncome: 5919.83, depositedIncome: 71037.96,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 712, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member65 — Shirley Hamilton
  '2034093c-1733-4514-ad3d-f9e8f66d9201': {
    firstName: 'Shirley', lastName: 'Hamilton',
    displayName: 'Shirley Hamilton', demoLabel: 'Demo Member65', avatarLabel: 'SH',
    photoUrl: `${PHOTO_BASE}/female_asian_1.png`,
    city: 'Lake Donald', state: 'AS', age: 21,
    loanAdvisor: 'Julia Rivera', memberSince: '2019-01-16',
    dashboardStatus: 'Dashboard Ready',
    email: 'demo.member65@verascore.test', phoneNumber: '+15550101012', zipcode: '206024226',
    verascore: 17, verascoreRating: 'At Risk', scoreMonth: '2026-04',
    sixMonthScore: 19, twelveMonthScore: 19, twentyFourMonthScore: 25,
    yearlyGrossIncome: 145617.00, monthlyGrossIncome: 12134.75, depositedIncome: 145617.00,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 658, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member70 — Pamela Williams
  '00d7c60d-7316-4ca7-b05a-f503c1a9824f': {
    firstName: 'Pamela', lastName: 'Williams',
    displayName: 'Pamela Williams', demoLabel: 'Demo Member70', avatarLabel: 'PW',
    photoUrl: `${PHOTO_BASE}/female_native_american_1.png`,
    city: 'Port Douglas', state: 'KY', age: 50,
    loanAdvisor: 'Jerry Alvarado', memberSince: '2015-06-15',
    dashboardStatus: 'Dashboard Ready',
    email: 'demo.member70@verascore.test', phoneNumber: '+15550101013', zipcode: '207749091',
    verascore: 32, verascoreRating: 'Poor', scoreMonth: '2026-04',
    sixMonthScore: 43, twelveMonthScore: 42, twentyFourMonthScore: 42,
    yearlyGrossIncome: 98880, monthlyGrossIncome: 8240.00, depositedIncome: 98880,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 685, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member74 — Thomas Taylor
  '3e786327-733e-4583-bcb8-9610d2912c11': {
    firstName: 'Thomas', lastName: 'Taylor',
    displayName: 'Thomas Taylor', demoLabel: 'Demo Member74', avatarLabel: 'TT',
    photoUrl: `${PHOTO_BASE}/male_african_american_1.png`,
    city: 'North Katherine', state: 'NM', age: 37,
    loanAdvisor: 'Adam Miles', memberSince: '2018-07-10',
    dashboardStatus: 'Needs Attention',
    email: 'demo.member74@verascore.test', phoneNumber: '+15550101014', zipcode: '207434217',
    verascore: 47, verascoreRating: 'Fair', scoreMonth: '2026-04',
    sixMonthScore: 42, twelveMonthScore: 38, twentyFourMonthScore: 41,
    yearlyGrossIncome: 72906.96, monthlyGrossIncome: 6075.58, depositedIncome: 72906.96,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 670, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member78 — Angela Petersen
  '12e4dbc8-3746-419a-aa32-1ab328fb1eb9': {
    firstName: 'Angela', lastName: 'Petersen',
    displayName: 'Angela Petersen', demoLabel: 'Demo Member78', avatarLabel: 'AP',
    photoUrl: `${PHOTO_BASE}/female_white_2.png`,
    city: 'North Joeltown', state: 'NC', age: 35,
    loanAdvisor: 'Kayla Moses', memberSince: '2022-08-13',
    dashboardStatus: 'Dashboard Ready',
    email: 'demo.member78@verascore.test', phoneNumber: '+15550101015', zipcode: '200200000',
    verascore: 56, verascoreRating: 'Fair', scoreMonth: '2026-04',
    sixMonthScore: 49, twelveMonthScore: 53, twentyFourMonthScore: 52,
    yearlyGrossIncome: 96000, monthlyGrossIncome: 8000.00, depositedIncome: 96000,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 725, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member82 — Jessica Petersen
  '0f120ade-5a25-42a9-8a3c-5f5c5bd8cc70': {
    firstName: 'Jessica', lastName: 'Petersen',
    displayName: 'Jessica Petersen', demoLabel: 'Demo Member82', avatarLabel: 'JP',
    photoUrl: `${PHOTO_BASE}/female_asian_3.png`,
    city: 'Matthewton', state: 'LA', age: 36,
    loanAdvisor: 'John Torres', memberSince: '2025-01-20',
    dashboardStatus: 'Dashboard Ready',
    email: 'demo.member82@verascore.test', phoneNumber: '+15550101016', zipcode: '207702456',
    verascore: 32, verascoreRating: 'Poor', scoreMonth: '2026-04',
    sixMonthScore: 44, twelveMonthScore: 42, twentyFourMonthScore: 43,
    yearlyGrossIncome: 111749.04, monthlyGrossIncome: 9312.42, depositedIncome: 111749.04,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 690, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member83 — Timothy Brown
  '2e65131d-2d27-4194-a2d5-74cec5a89166': {
    firstName: 'Timothy', lastName: 'Brown',
    displayName: 'Timothy Brown', demoLabel: 'Demo Member83', avatarLabel: 'TB',
    photoUrl: `${PHOTO_BASE}/male_native_american_1.png`,
    city: 'North Johnland', state: 'PW', age: 43,
    loanAdvisor: 'Amy Rose', memberSince: '2019-08-23',
    dashboardStatus: 'Dashboard Ready',
    email: 'demo.member83@verascore.test', phoneNumber: '+15550101017', zipcode: '207746069',
    verascore: 68, verascoreRating: 'Good', scoreMonth: '2026-04',
    sixMonthScore: 50, twelveMonthScore: 50, twentyFourMonthScore: 52,
    yearlyGrossIncome: 182894.4, monthlyGrossIncome: 15241.20, depositedIncome: 182894.4,
    incomeBasisUsed: 'DEPOSITED_NET', incomeConfidence: 0.85,
    creditScore: 738, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member87 — Mark Jackson
  '2826e44e-27d1-40f6-8291-8dab6bd6cf90': {
    firstName: 'Mark', lastName: 'Jackson',
    displayName: 'Mark Jackson', demoLabel: 'Demo Member87', avatarLabel: 'MJ',
    photoUrl: `${PHOTO_BASE}/male_native_american_1.png`,
    city: 'East Kevinbury', state: 'WV', age: 35,
    loanAdvisor: 'Keith Meyer', memberSince: '2018-12-31',
    dashboardStatus: 'Dashboard Ready',
    email: 'demo.member87@verascore.test', phoneNumber: '+15550101018', zipcode: '209104275',
    verascore: 85, verascoreRating: 'Excellent', scoreMonth: '2026-04',
    sixMonthScore: 76, twelveMonthScore: 72, twentyFourMonthScore: 70,
    yearlyGrossIncome: 183500.04, monthlyGrossIncome: 15291.67, depositedIncome: 183500.04,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 755, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member8 — Andrea Blake
  '974c7091-ec6b-47e8-89d7-e53804978f11': {
    firstName: 'Andrea', lastName: 'Blake',
    displayName: 'Andrea Blake', demoLabel: 'Demo Member8', avatarLabel: 'AB',
    photoUrl: `${PHOTO_BASE}/female_white_1.png`,
    city: 'East Jason', state: 'AS', age: 19,
    loanAdvisor: 'Emily Byrd', memberSince: '2017-04-27',
    dashboardStatus: 'Awaiting Dashboard',
    email: 'demo.member8@verascore.test', phoneNumber: '+15550101019', zipcode: '207446940',
    verascore: 23, verascoreRating: 'Poor', scoreMonth: '2026-04',
    sixMonthScore: 24, twelveMonthScore: 31, twentyFourMonthScore: 37,
    yearlyGrossIncome: 66999.96, monthlyGrossIncome: 5583.33, depositedIncome: 66999.96,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 663, creditScoreSource: 'DEMO_SYNTHETIC',
  },
  // Member9 — Terry Jones
  '0d0db712-212a-4e02-9727-562f4311ad6f': {
    firstName: 'Terry', lastName: 'Jones',
    displayName: 'Terry Jones', demoLabel: 'Demo Member9', avatarLabel: 'TJ',
    photoUrl: `${PHOTO_BASE}/male_asian_2.png`,
    city: 'Kristenbury', state: 'PW', age: 30,
    loanAdvisor: 'Dustin Holland', memberSince: '2017-09-29',
    dashboardStatus: 'Awaiting Dashboard',
    email: 'demo.member9@verascore.test', phoneNumber: '+15550101020', zipcode: '207843259',
    verascore: 43, verascoreRating: 'Fair', scoreMonth: '2026-04',
    sixMonthScore: 43, twelveMonthScore: 44, twentyFourMonthScore: 49,
    yearlyGrossIncome: 39999.96, monthlyGrossIncome: 3333.33, depositedIncome: 39999.96,
    incomeBasisUsed: 'DEPOSITED_NET',
    creditScore: 706, creditScoreSource: 'DEMO_SYNTHETIC',
  },
};

// "D.", "D", "M.", "M" all count as redacted. Empty also counts as redacted.
const REDACTED_NAME_PATTERN = /^[A-Z]\.?$/;

const looksRedacted = (value: string | null | undefined): boolean => {
  if (!value) return true;
  return REDACTED_NAME_PATTERN.test(value.trim());
};

const looksRedactedFullName = (value: string | null | undefined): boolean => {
  if (!value) return true;
  // Matches "D. M.", "D M", and similar 1-2 letter initials-only patterns.
  return /^[A-Z]\.?(\s+[A-Z]\.?)*$/.test(value.trim());
};

const isMissingNumeric = (value: number | null | undefined): boolean => {
  return value == null || value === 0;
};

const isMissingString = (value: string | null | undefined): boolean => {
  return value == null || value.trim() === '';
};

/**
 * Apply Demo2 overlay to a single LenderMember.
 * Returns the original object untouched for unknown member_ids (which scopes
 * the overlay to Demo CU implicitly, since only Demo CU UUIDs are in the map).
 *
 * Fill rules:
 *   - Name fields: replace only when API value is empty / "D." / "M." style.
 *   - Profile fields (photo, age, city, state, advisor, since, status): fill
 *     only when API value is null / empty.
 *   - Score fields: fill only when API value is null / 0.
 *   - Income fields: fill only when API value is null / 0 (yearly/monthly) or
 *     null (deposited).
 *   - For Member27 the persona intentionally omits score fields, so the
 *     UI continues to show the empty-score state.
 */
export function applyDemo2PersonaOverlay(lm: LenderMember): LenderMember {
  if (!lm || !lm.member_id) return lm;
  const persona = DEMO2_PERSONA_MAP[lm.member_id];
  if (!persona) return lm;

  const overlaid: LenderMember = { ...lm };
  let changed = false;

  // --- Names ---
  if (looksRedacted(overlaid.first_name)) {
    overlaid.first_name = persona.firstName;
    changed = true;
  }
  if (looksRedacted(overlaid.last_name)) {
    overlaid.last_name = persona.lastName;
    changed = true;
  }
  if (isMissingString(overlaid.member_name) || looksRedactedFullName(overlaid.member_name)) {
    overlaid.member_name = persona.displayName;
    changed = true;
  }

  // --- Profile (members snapshot) ---
  if (persona.photoUrl && isMissingString(overlaid.photo)) {
    overlaid.photo = persona.photoUrl;
    changed = true;
  }
  if (persona.age != null && (overlaid.age == null || overlaid.age === 0)) {
    overlaid.age = persona.age;
    changed = true;
  }
  if (persona.city && isMissingString(overlaid.city)) {
    overlaid.city = persona.city;
    changed = true;
  }
  if (persona.state && isMissingString(overlaid.state)) {
    overlaid.state = persona.state;
    changed = true;
  }
  if (persona.loanAdvisor && isMissingString(overlaid.inst_loan_advisor)) {
    overlaid.inst_loan_advisor = persona.loanAdvisor;
    changed = true;
  }
  if (persona.memberSince && isMissingString(overlaid.inst_member_since)) {
    overlaid.inst_member_since = persona.memberSince;
    changed = true;
  }
  if (persona.dashboardStatus && isMissingString(overlaid.dashboard_status)) {
    overlaid.dashboard_status = persona.dashboardStatus;
    changed = true;
  }

  // --- Contact (Phase 12B) ---
  if (persona.email && isMissingString(overlaid.email)) {
    overlaid.email = persona.email;
    changed = true;
  }
  if (persona.phoneNumber && isMissingString(overlaid.phone_number)) {
    overlaid.phone_number = persona.phoneNumber;
    changed = true;
  }
  if (persona.zipcode && isMissingString(overlaid.zipcode)) {
    overlaid.zipcode = persona.zipcode;
    changed = true;
  }

  // --- Score (lender_member_summary) ---
  if (persona.verascore != null && isMissingNumeric(overlaid.verascore)) {
    overlaid.verascore = persona.verascore;
    changed = true;
  }
  if (persona.verascoreRating && isMissingString(overlaid.verascore_rating)) {
    overlaid.verascore_rating = persona.verascoreRating;
    changed = true;
  }
  if (persona.scoreMonth && isMissingString(overlaid.score_month)) {
    overlaid.score_month = persona.scoreMonth;
    changed = true;
  }
  if (persona.sixMonthScore != null && isMissingNumeric(overlaid.six_month_score)) {
    overlaid.six_month_score = persona.sixMonthScore;
    changed = true;
  }
  if (persona.twelveMonthScore != null && isMissingNumeric(overlaid.twelve_month_score)) {
    overlaid.twelve_month_score = persona.twelveMonthScore;
    changed = true;
  }
  if (persona.twentyFourMonthScore != null && isMissingNumeric(overlaid.twenty_four_month_score)) {
    overlaid.twenty_four_month_score = persona.twentyFourMonthScore;
    changed = true;
  }

  // --- Income ---
  if (persona.yearlyGrossIncome != null && isMissingNumeric(overlaid.yearly_gross_income)) {
    overlaid.yearly_gross_income = persona.yearlyGrossIncome;
    changed = true;
  }
  if (persona.monthlyGrossIncome != null && isMissingNumeric(overlaid.monthly_gross_income)) {
    overlaid.monthly_gross_income = persona.monthlyGrossIncome;
    changed = true;
  }
  if (persona.depositedIncome != null && overlaid.deposited_income == null) {
    overlaid.deposited_income = persona.depositedIncome;
    changed = true;
  }
  if (persona.incomeBasisUsed && overlaid.income_basis_used == null) {
    overlaid.income_basis_used = persona.incomeBasisUsed;
    changed = true;
  }
  if (persona.incomeConfidence != null && overlaid.income_confidence == null) {
    overlaid.income_confidence = persona.incomeConfidence;
    changed = true;
  }

  // --- Credit score (synthetic; credit_scores table is empty for Demo CU) ---
  if (persona.creditScore != null && (overlaid.credit_score == null || overlaid.credit_score === 0)) {
    overlaid.credit_score = persona.creditScore;
    changed = true;
  }
  if (persona.creditScoreSource && overlaid.credit_score_source == null) {
    overlaid.credit_score_source = persona.creditScoreSource;
    changed = true;
  }

  return changed ? overlaid : lm;
}

/**
 * Membership check for the Demo2 persona scope. Returns true only for member
 * UUIDs present in DEMO2_PERSONA_MAP. Used as the gate for Demo2-only display
 * behaviors (Phase 14 month-shift, etc.) without exposing the persona map.
 */
export function isDemo2Member(memberId: string | null | undefined): boolean {
  if (!memberId) return false;
  return Object.prototype.hasOwnProperty.call(DEMO2_PERSONA_MAP, memberId);
}

/**
 * Apply overlay to a full members list. Safe for non-Demo orgs (pass-through).
 */
export function applyDemo2PersonaOverlayList(members: LenderMember[]): LenderMember[] {
  if (!Array.isArray(members) || members.length === 0) return members;
  let appliedCount = 0;
  const out = members.map((lm) => {
    const overlaid = applyDemo2PersonaOverlay(lm);
    if (overlaid !== lm) appliedCount += 1;
    return overlaid;
  });
  if (appliedCount > 0) {
    console.info(`[Demo2Overlay] applied to ${appliedCount}/${members.length} members`);
  }
  return out;
}
